import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Segmented, Spin } from 'antd';
import HighFidelityYieldChart from './HighFidelityYieldChart';
import apiClient from '../../services/apiClient';
import { fetchYieldPayload } from '../../utils/yieldApiUtils';
import './CorporateYieldCurvesRedesign.css';

const yieldCurveConfigs = [
  { id: 'aaa-psu-secured', title: 'AAA PSU Secured', rating: 'AAA', issuerType: 'psu', securityType: 'secured' },
  { id: 'aaa-psu-unsecured', title: 'AAA PSU Unsecured', rating: 'AAA', issuerType: 'psu', securityType: 'unsecured' },
  { id: 'aaa-non-psu-secured', title: 'AAA Non-PSU Secured', rating: 'AAA', issuerType: 'non-psu', securityType: 'secured' },
  { id: 'aaa-non-psu-unsecured', title: 'AAA Non-PSU Unsecured', rating: 'AAA', issuerType: 'non-psu', securityType: 'unsecured' },
  { id: 'aa-plus-psu-secured', title: 'AA+ PSU Secured', rating: 'AA+', issuerType: 'psu', securityType: 'secured' },
  { id: 'aa-plus-psu-unsecured', title: 'AA+ PSU Unsecured', rating: 'AA+', issuerType: 'psu', securityType: 'unsecured' },
  { id: 'aa-plus-non-psu-secured', title: 'AA+ Non-PSU Secured', rating: 'AA+', issuerType: 'non-psu', securityType: 'secured' },
  { id: 'aa-plus-non-psu-unsecured', title: 'AA+ Non-PSU Unsecured', rating: 'AA+', issuerType: 'non-psu', securityType: 'unsecured' },
  { id: 'aa-psu-secured', title: 'AA PSU Secured', rating: 'AA', issuerType: 'psu', securityType: 'secured' },
  { id: 'aa-psu-unsecured', title: 'AA PSU Unsecured', rating: 'AA', issuerType: 'psu', securityType: 'unsecured' },
  { id: 'aa-non-psu-secured', title: 'AA Non-PSU Secured', rating: 'AA', issuerType: 'non-psu', securityType: 'secured' },
  { id: 'aa-non-psu-unsecured', title: 'AA Non-PSU Unsecured', rating: 'AA', issuerType: 'non-psu', securityType: 'unsecured' },
  { id: 'aa-minus-psu-secured', title: 'AA- PSU Secured', rating: 'AA-', issuerType: 'psu', securityType: 'secured' },
  { id: 'aa-minus-psu-unsecured', title: 'AA- PSU Unsecured', rating: 'AA-', issuerType: 'psu', securityType: 'unsecured' },
  { id: 'aa-minus-non-psu-secured', title: 'AA- Non-PSU Secured', rating: 'AA-', issuerType: 'non-psu', securityType: 'secured' },
  { id: 'aa-minus-non-psu-unsecured', title: 'AA- Non-PSU Unsecured', rating: 'AA-', issuerType: 'non-psu', securityType: 'unsecured' },
  { id: 'a-plus-psu-secured', title: 'A+ PSU Secured', rating: 'A+', issuerType: 'psu', securityType: 'secured' },
  { id: 'a-plus-psu-unsecured', title: 'A+ PSU Unsecured', rating: 'A+', issuerType: 'psu', securityType: 'unsecured' },
  { id: 'a-plus-non-psu-secured', title: 'A+ Non-PSU Secured', rating: 'A+', issuerType: 'non-psu', securityType: 'secured' },
  { id: 'a-plus-non-psu-unsecured', title: 'A+ Non-PSU Unsecured', rating: 'A+', issuerType: 'non-psu', securityType: 'unsecured' },
  { id: 'a-psu-secured', title: 'A PSU Secured', rating: 'A', issuerType: 'psu', securityType: 'secured' },
  { id: 'a-psu-unsecured', title: 'A PSU Unsecured', rating: 'A', issuerType: 'psu', securityType: 'unsecured' },
  { id: 'a-non-psu-secured', title: 'A Non-PSU Secured', rating: 'A', issuerType: 'non-psu', securityType: 'secured' },
  { id: 'a-non-psu-unsecured', title: 'A Non-PSU Unsecured', rating: 'A', issuerType: 'non-psu', securityType: 'unsecured' },
];

const countYieldPoints = (payload) => {
  let raw = [];
  if (Array.isArray(payload)) raw = payload;
  else if (payload && Array.isArray(payload.yieldData)) raw = payload.yieldData;
  return raw.filter((item) => {
    const year = Number(item?.year);
    const yld = Number(item?.yield);
    return !Number.isNaN(year) && !Number.isNaN(yld);
  }).length;
};

const fetchOneCurve = async (config) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const queryParams = new URLSearchParams({
    rating: config.rating,
    issuerType: config.issuerType,
    securityType: config.securityType,
  });
  const apiUrl = `${baseUrl}/yield/bonds?${queryParams.toString()}`;
  try {
    const payload = await fetchYieldPayload(apiClient, apiUrl);
    return {
      yieldData: payload?.yieldData || [],
      topTrades: payload?.topBondsTradeValues || [],
      error: null,
    };
  } catch (err) {
    console.error(`Error fetching ${config.id}:`, err);
    return {
      yieldData: [],
      topTrades: [],
      error: err.message || 'Failed to load',
    };
  }
};

/**
 * Corporate curves: concurrency = 1.
 * - Load first curve on rating change
 * - Load the next only after the previous finishes AND the user scrolls near the sentinel
 * Never fires 4 requests in parallel.
 */
const CorporateYieldCurvesRedesign = () => {
  const [activeRating, setActiveRating] = useState('AAA');
  const [cache, setCache] = useState({});
  /** How many configs for the active rating have been requested (0..N). */
  const [fetchCursor, setFetchCursor] = useState(0);
  const [loadingId, setLoadingId] = useState(null);
  const [awaitingScroll, setAwaitingScroll] = useState(false);
  const sentinelRef = useRef(null);
  const inFlightRef = useRef(false);

  const ratingGroups = useMemo(
    () =>
      yieldCurveConfigs.reduce((acc, config) => {
        if (!acc[config.rating]) acc[config.rating] = [];
        acc[config.rating].push(config);
        return acc;
      }, {}),
    []
  );

  const activeConfigs = ratingGroups[activeRating] || [];

  // Reset cursor when rating changes — only queue the first curve
  useEffect(() => {
    setFetchCursor(0);
    setAwaitingScroll(false);
    inFlightRef.current = false;
    setLoadingId(null);
    // Kick first fetch for this rating if not cached
    if (activeConfigs[0] && !cache[activeConfigs[0].id]) {
      setFetchCursor(1);
    } else if (activeConfigs[0] && cache[activeConfigs[0].id]) {
      // First already cached — wait for scroll before next
      setFetchCursor(1);
      setAwaitingScroll(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on rating change
  }, [activeRating]);

  // Single in-flight fetch: process fetchCursor - 1
  useEffect(() => {
    if (fetchCursor < 1) return undefined;
    const index = fetchCursor - 1;
    const config = activeConfigs[index];
    if (!config) return undefined;
    if (cache[config.id]) {
      const entry = cache[config.id];
      const hasPoints =
        !entry.error && countYieldPoints(entry.yieldData) >= 1;
      if (fetchCursor < activeConfigs.length) {
        if (hasPoints) {
          setAwaitingScroll(true);
        } else {
          setFetchCursor((c) => Math.min(c + 1, activeConfigs.length));
        }
      }
      return undefined;
    }
    if (inFlightRef.current) return undefined;

    let cancelled = false;
    inFlightRef.current = true;
    setLoadingId(config.id);
    setAwaitingScroll(false);

    (async () => {
      const result = await fetchOneCurve(config);
      if (cancelled) return;
      setCache((prev) => ({ ...prev, [config.id]: result }));
      setLoadingId(null);
      inFlightRef.current = false;

      const hasPoints =
        !result.error && countYieldPoints(result.yieldData) >= 1;
      const hasMore = fetchCursor < activeConfigs.length;

      if (!hasMore) return;

      if (hasPoints) {
        // Wait for user to scroll before loading the next curve
        setAwaitingScroll(true);
      } else {
        // Empty — advance immediately, still one-at-a-time
        setFetchCursor((c) => Math.min(c + 1, activeConfigs.length));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchCursor, activeConfigs, cache]);

  // Sentinel: only advances cursor when user scrolls near it (and previous finished)
  useEffect(() => {
    if (!awaitingScroll) return undefined;
    if (fetchCursor >= activeConfigs.length) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (inFlightRef.current) return;
        setAwaitingScroll(false);
        setFetchCursor((c) => Math.min(c + 1, activeConfigs.length));
      },
      { root: null, rootMargin: '80px 0px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [awaitingScroll, fetchCursor, activeConfigs.length]);

  const visibleCharts = activeConfigs.filter((config) => {
    const entry = cache[config.id];
    if (!entry) return false;
    if (entry.error) return true;
    return countYieldPoints(entry.yieldData) >= 1;
  });

  const loadingConfig = loadingId
    ? activeConfigs.find((c) => c.id === loadingId)
    : null;

  const allDone =
    activeConfigs.length > 0 &&
    activeConfigs.every((c) => cache[c.id]) &&
    !loadingId;

  const anyWithData = visibleCharts.length > 0;

  return (
    <div className="corporate-yield-curves-section visible">
      <div className="corporate-yield-rating-sticky">
        <div className="capsule-selectors-container">
          <div className="capsule-group">
            <Segmented
              value={activeRating}
              onChange={(val) => setActiveRating(val)}
              options={Object.keys(ratingGroups).map((r) => ({ label: r, value: r }))}
              className="capsule-segmented"
            />
          </div>
        </div>
      </div>

      <div className="corporate-charts-main-content">
        <div className="corporate-yield-charts-container">
          {visibleCharts.map((config) => {
            const entry = cache[config.id];
            return (
              <div key={config.id} className="corporate-yield-charts-scroll">
                <HighFidelityYieldChart
                  apiData={entry.yieldData}
                  type="Corporate"
                  isLoading={false}
                  error={entry.error}
                  topTrades={entry.topTrades}
                  tradeDate={entry.topTrades?.[0]?.tradeDate}
                  title={config.title}
                  color="#0f172a"
                />
              </div>
            );
          })}

          {loadingConfig && (
            <div className="corporate-yield-slot-loading">
              <Spin />
              <span>{loadingConfig.title}</span>
            </div>
          )}

          {/* Advances to next API only when scrolled near — never parallel */}
          {awaitingScroll && fetchCursor < activeConfigs.length && (
            <div ref={sentinelRef} className="corporate-yield-load-sentinel" aria-hidden />
          )}

          {allDone && !anyWithData && (
            <div className="corporate-yield-empty">No yield data for {activeRating}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorporateYieldCurvesRedesign;
